import { PlusOutlined } from '@ant-design/icons';
import { PERMISSIONS } from '@navicosa/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import { useState } from 'react';
import { api, errorText } from '../../api/client';
import type { Supplier } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';

/** Đối tác/nhà cung cấp – thông tin nội bộ, không hiển thị cho khách (PRD 2.10). */
export function SuppliersPage() {
  const { can } = useAuth();
  const canEdit = can(PERMISSIONS.SUPPLIERS_MANAGE);
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Supplier | 'new' | null>(null);
  const [form] = Form.useForm();
  const list = useQuery({
    queryKey: ['suppliers', q, page],
    queryFn: () => api.page<Supplier>('/admin/suppliers', { q, page, limit: 20 }),
  });

  const submit = async () => {
    const v = await form.validateFields();
    try {
      if (editing === 'new') await api.post('/admin/suppliers', v);
      else if (editing) await api.patch(`/admin/suppliers/${editing.id}`, v);
      message.success('Đã lưu');
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ['suppliers'] });
    } catch (e) {
      message.error(errorText(e));
    }
  };

  return (
    <Card
      title="Đối tác / nhà cung cấp"
      extra={
        canEdit && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => (form.resetFields(), setEditing('new'))}
          >
            Thêm đối tác
          </Button>
        )
      }
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Thông tin nội bộ – không bao giờ hiển thị cho khách trên Mini App/website."
      />
      <Input.Search
        placeholder="Tìm tên, mã, SĐT"
        allowClear
        onSearch={(v) => (setQ(v), setPage(1))}
        style={{ width: 300, marginBottom: 16 }}
      />
      <Table<Supplier>
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data?.items}
        pagination={{
          current: page,
          total: list.data?.meta.total,
          pageSize: 20,
          onChange: setPage,
        }}
        columns={[
          { title: 'Tên', dataIndex: 'name' },
          { title: 'Mã', dataIndex: 'supplier_code' },
          {
            title: 'Liên hệ',
            render: (_, r) =>
              [r.contact_name, r.phone?.replace('+84', '0')].filter(Boolean).join(' · '),
          },
          {
            title: 'Giá nhập đang dùng',
            align: 'right',
            render: (_, r) => r._count?.supplier_prices ?? 0,
          },
          {
            title: 'Trạng thái',
            render: (_, r) => (
              <Tag color={r.status === 'active' ? 'green' : 'default'}>
                {r.status === 'active' ? 'Hoạt động' : 'Ngừng'}
              </Tag>
            ),
          },
          ...(canEdit
            ? [
                {
                  title: '',
                  render: (_: unknown, r: Supplier) => (
                    <Space>
                      <Button
                        size="small"
                        onClick={() => (
                          form.setFieldsValue({ ...r, phone: r.phone?.replace('+84', '0') }),
                          setEditing(r)
                        )}
                      >
                        Sửa
                      </Button>
                      <Popconfirm
                        title="Xóa đối tác? Giá nhập của đối tác sẽ ngừng dùng."
                        onConfirm={async () => {
                          try {
                            await api.del(`/admin/suppliers/${r.id}`);
                            void qc.invalidateQueries({ queryKey: ['suppliers'] });
                          } catch (e) {
                            message.error(errorText(e));
                          }
                        }}
                      >
                        <Button size="small" danger>
                          Xóa
                        </Button>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]
            : []),
        ]}
      />
      <Modal
        open={editing !== null}
        title={editing === 'new' ? 'Thêm đối tác' : 'Sửa đối tác'}
        onCancel={() => setEditing(null)}
        onOk={() => void submit()}
        okText="Lưu"
        forceRender
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Tên đối tác" rules={[{ required: true, min: 2 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="supplier_code" label="Mã đối tác">
            <Input />
          </Form.Item>
          <Space wrap>
            <Form.Item name="contact_name" label="Người liên hệ">
              <Input />
            </Form.Item>
            <Form.Item name="phone" label="Số điện thoại">
              <Input />
            </Form.Item>
          </Space>
          <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="tax_code" label="Mã số thuế">
            <Input />
          </Form.Item>
          <Form.Item name="address" label="Địa chỉ / bãi xe">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="notes" label="Ghi chú (thế mạnh, khu vực, chất lượng…)">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="status" label="Trạng thái" initialValue="active">
            <Select
              options={[
                { value: 'active', label: 'Hoạt động' },
                { value: 'inactive', label: 'Ngừng' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
